import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Error from '@components/error'
import SliderNew from '@components/slider-new'
import Slider from 'react-slick'
import Container from '@material-ui/core/Container'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import gql from 'graphql-tag'
import LinearProgress from '@material-ui/core/LinearProgress'
import { useQuery } from '@apollo/client'
import AcademyCoachCard from './academy-coach-card/academy-coach-card'
import { useStyles } from './academy.styles'

const COACHES_QUERY = gql`
  query academyCoachesQuery($id: ID) {
    academy(id: $id) {
      id
      slug
      name
      description
      logoImageUrl
      featuredImageUrl
      promotedContent
      employments {
        id
        role
        publiclyVisible
        coach {
          id
          name
          profileUrl
          bookingUrl
          coachProfile {
            bio
            profilePhoto
            slug
            title
            firstName
            lastName
          }
        }
      }
    }
  }
`

const visiblySortedCoaches = (employments) => {
  const visibleEmployments = employments?.filter(
    employment => employment.publiclyVisible === true,
  )
  // this sorting is only valid as long as we have only two roles: coach and owner
  // Sorts by role, then by Coach last name to return an array of coaches
  return visibleEmployments
    ?.sort((coachA, coachB) => {
      if (coachA.role === coachB.role) {
        return coachA.coach.name
          .split(' ')
          .slice(-1)[0]
          .localeCompare(coachB.coach.name.split(' ').slice(-1)[0])
      }
      return coachA.role.localeCompare(coachB.role)
    })
    .map(employment => employment.coach)
}

const settings = (employedCoaches, paginationNumber, isClient, isHOPEAcademy = false) => {
  return {
    dots: true,
    infinite: true,
    slidesToShow: Math.min(employedCoaches?.length, 4),
    slidesToScroll: 4,
    initialSlide: 0,
    rows: isHOPEAcademy ? 2 : 1,
    customPaging(i) {
      return (
        <a className={paginationNumber}>
          {i < 9 && 0}
          {i + 1}
        </a>
      )
    },
    responsive: isClient
      ? [
        {
          breakpoint: 1300,
          settings: {
            slidesToShow: Math.min(employedCoaches?.length, 3),
            slidesToScroll: 3,
            swipeToSlide: true,
          },
        },
        {
          breakpoint: 960,
          settings: {
            slidesToShow: Math.min(employedCoaches?.length, 2),
            slidesToScroll: 2,
            swipeToSlide: true,
          },
        },
        {
          breakpoint: 600,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            swipeToSlide: true,
          },
        },
      ]
      : null,
  }
}

const MobileCoachCards = ({ controlClassName, coaches, isHOPEAcademy }) => {
  return (
    <SliderNew controlClassName={controlClassName}>
      {coaches?.map((coach) => {
        return <AcademyCoachCard key={coach.id} coach={coach} isHOPECoach={isHOPEAcademy} />
      })}
    </SliderNew>
  )
}

const CoachCardSlider = ({ coaches, isClient, isHOPEAcademy }) => {
  const classes = useStyles()
  return (
    <Slider
      key={`coaches-slider-${isClient ? 'client' : 'server'}`}
      {...settings(coaches, classes.paginationNumber, isClient, isHOPEAcademy)}
      className={`${classes.eventsWrapper}`}
    >
      {coaches.map(coach => (
        <AcademyCoachCard key={coach.id} coach={coach} isHOPECoach={isHOPEAcademy} />
      ))}
    </Slider>
  )
}

const CoachCards = ({ coaches, isHOPEAcademy }) => {
  if (coaches?.length === 1) {
    return (
      <Grid container style={{ justifyContent: 'center' }}>
        <AcademyCoachCard coach={coaches[0]} isSolo isHOPECoach={isHOPEAcademy} />
      </Grid>
    )
  }
  return (
    <Grid container style={{ justifyContent: 'center' }}>
      {coaches?.map(coach => (
        <Grid
          key={coach.id}
          item
          md={4}
          lg={3}
          style={{ display: 'flex' }}
        >
          <AcademyCoachCard key={coach.id} coach={coach} isSolo={false} />
        </Grid>
      ))}
    </Grid>
  )
}

const Coaches = ({
  academy, title, padding, bgColor,
}) => {
  const { id, slug } = academy
  const isHOPEAcademy = slug.startsWith('pga-hope-')
  const classes = useStyles({ padding })
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))
  const altBackgroundColor = bgColor || '#F5F7FA'

  const [isClient, setIsClient] = useState(false)
  useEffect(() => {
    setIsClient(true)
  }, [])

  const { loading, data, error } = useQuery(COACHES_QUERY, {
    variables: { id },
    context: { clientName: 'coach-tools' },
  })

  if (loading) return <LinearProgress color="secondary" />
  if (error) return <Error error={error} displayErrorType="page" />
  const coaches = visiblySortedCoaches(data?.academy?.employments)
  return (
    coaches?.length > 0 && (
      <Box className={classes.coachesSection} style={{ backgroundColor: altBackgroundColor }}>
        <Container style={{ marginBottom: coaches.length > 8 && !isMobile ? '32px' : '0px' }}>
          <Typography
            variant="h4"
            color="secondary"
            className={classes.sectionHeading}
          >
            {title || 'Our Coaching Staff'}
          </Typography>
          {isMobile || !isHOPEAcademy ? (
            <MobileCoachCards
              controlClassName={classes.controlClassName}
              coaches={coaches}
              isHOPEAcademy={isHOPEAcademy}
            />
          ) : (
            <>
              {coaches.length > 8 ? (
                <CoachCardSlider
                  coaches={coaches}
                  isClient={isClient}
                  isHOPEAcademy={isHOPEAcademy}
                />
              ) : (
                <CoachCards coaches={coaches} isHOPEAcademy={isHOPEAcademy} />
              )}
            </>
          )}
        </Container>
      </Box>
    )
  )
}

Coaches.propTypes = {
  academy: PropTypes.shape({
    id: PropTypes.string,
    slug: PropTypes.string,
    employments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        role: PropTypes.string,
        publiclyVisible: PropTypes.bool,
        coach: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          bookingUrl: PropTypes.string,
          profileUrl: PropTypes.string,
          coachProfile: PropTypes.shape({
            bio: PropTypes.string,
            profilePhoto: PropTypes.string,
            title: PropTypes.string,
            firstName: PropTypes.string,
            lastName: PropTypes.string,
          }),
        }),
      }),
    ),
    title: PropTypes.string,
    padding: PropTypes.string,
    coaches: PropTypes.arrayOf(PropTypes.object),
  }),
  title: PropTypes.string,
  padding: PropTypes.string,
  bgColor: PropTypes.string,
}
CoachCardSlider.propTypes = {
  coaches: PropTypes.arrayOf(PropTypes.object),
  isClient: PropTypes.bool,
  isHOPEAcademy: PropTypes.bool,
}
CoachCards.propTypes = {
  coaches: PropTypes.arrayOf(PropTypes.object),
  isHOPEAcademy: PropTypes.bool,
}
MobileCoachCards.propTypes = {
  controlClassName: PropTypes.string,
  coaches: PropTypes.arrayOf(PropTypes.object),
  isHOPEAcademy: PropTypes.bool,
}

export default Coaches
